import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, DollarSign, CheckCircle2, Clock, Trash2, Users, RefreshCw } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const STATUS = {
  sent: { label: 'Invite sent', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  checkout_started: { label: 'Checkout started', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  imported: { label: 'Active / Claimed', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  revoked: { label: 'Revoked', cls: 'bg-[#F1F5F9] text-[#64748B] border-[#E2E8F0]' },
};

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#94A3B8] text-xs uppercase font-bold mb-2"><Icon size={14} className={accent} />{label}</div>
      <div className="text-2xl font-bold text-[#0A0F1C]">{value}</div>
    </div>
  );
}

export default function AdminSharedPlans() {
  const [shares, setShares] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API_URL}/api/admin/social-studio/shares`, { withCredentials: true })
      .then((r) => setShares(r.data.shares || []))
      .catch(() => setShares(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const revoke = async (inviteId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/social-studio/shares/${inviteId}`, { withCredentials: true });
      toast.success('Invite revoked');
      load();
    } catch (e) {
      toast.error('Revoke failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  if (shares === null) return <AdminLayout title="Shared Plans"><div className="p-8 text-center text-[#94A3B8]">Loading…</div></AdminLayout>;
  if (shares === false) return <AdminLayout title="Shared Plans"><div className="p-8 text-center text-red-600">Failed to load shared plans.</div></AdminLayout>;

  const active = shares.filter((s) => s.status === 'imported');
  const pending = shares.filter((s) => s.status === 'sent' || s.status === 'checkout_started');
  const mrr = active.reduce((sum, s) => sum + (s.price_usd || 0), 0);

  return (
    <AdminLayout title="Shared Plans">
      <div data-testid="admin-shared-plans" className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#0A0F1C]">Shared Plans</h1>
            <p className="text-sm text-[#64748B]">Track the content plans you've shared and the recurring revenue they generate.</p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1D4ED8] hover:underline" data-testid="shares-refresh"><RefreshCw size={14} /> Refresh</button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={Send} label="Total invites" value={shares.length} accent="text-[#1D4ED8]" testid="kpi-total" />
          <Kpi icon={Clock} label="Pending" value={pending.length} accent="text-amber-500" testid="kpi-pending" />
          <Kpi icon={CheckCircle2} label="Active subscribers" value={active.length} accent="text-emerald-600" testid="kpi-active" />
          <Kpi icon={DollarSign} label="Est. MRR" value={fmt(mrr)} accent="text-[#F97316]" testid="kpi-mrr" />
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
          {shares.length === 0 ? (
            <div className="p-10 text-center text-[#94A3B8]" data-testid="shares-empty">
              <Users size={28} className="mx-auto mb-3 text-[#CBD5E1]" />
              No shared plans yet. Share a plan from the Content Planner to invite another agent.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="shares-table">
                <thead>
                  <tr className="text-left text-[#94A3B8] text-xs uppercase tracking-wider border-b border-[#E2E8F0]">
                    <th className="px-4 py-3 font-bold">Recipient</th>
                    <th className="px-4 py-3 font-bold">Plan</th>
                    <th className="px-4 py-3 font-bold">Status</th>
                    <th className="px-4 py-3 font-bold">Price</th>
                    <th className="px-4 py-3 font-bold">Sent</th>
                    <th className="px-4 py-3 font-bold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shares.map((s) => {
                    const st = STATUS[s.status] || { label: s.status, cls: 'bg-[#F1F5F9] text-[#64748B] border-[#E2E8F0]' };
                    const canRevoke = s.status === 'sent' || s.status === 'checkout_started';
                    return (
                      <tr key={s.invite_id} className="border-b border-[#F1F5F9] last:border-0 hover:bg-[#FAFAFA]" data-testid={`share-row-${s.invite_id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-[#0A0F1C]">{s.recipient_name || '—'}</div>
                          <div className="text-xs text-[#94A3B8]">{s.recipient_email}</div>
                        </td>
                        <td className="px-4 py-3 text-[#475569]">{s.summary?.plan_title || '—'}<div className="text-xs text-[#94A3B8]">{s.summary?.days}-day · {s.summary?.brand_name}</div></td>
                        <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold border ${st.cls}`}>{st.label}</span></td>
                        <td className="px-4 py-3 font-semibold text-[#0A0F1C]">{fmt(s.price_usd)}<span className="text-xs text-[#94A3B8] font-normal">/mo</span></td>
                        <td className="px-4 py-3 text-[#64748B]">{fmtDate(s.created_at)}</td>
                        <td className="px-4 py-3 text-right">
                          {canRevoke ? (
                            <button onClick={() => revoke(s.invite_id)} className="text-[#94A3B8] hover:text-red-600 transition-colors" aria-label="Revoke invite" data-testid={`share-revoke-${s.invite_id}`}><Trash2 size={15} /></button>
                          ) : <span className="text-xs text-[#CBD5E1]">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
